import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    ListSubheader,
    Pagination,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    FormCheckbox,
    FormInputGroup,
    FormSection,
    FormSelect,
    FormText,
} from 'components/form';
import { FormSelectOption } from 'components/form/types';
import { useAuth } from 'hooks';
import { useFormContext } from 'react-hook-form';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { getGroups } from 'store/services/supplier';
import { BulkDevicesInput, SubscriberOption } from '../../BulkEditDevicesModal';
import {
    ArrowRightAlt,
    CheckBox,
    CheckBoxOutlineBlank,
    IndeterminateCheckBoxOutlined,
} from '@mui/icons-material';

interface BulkEditDevicesEditProps {
    subscriberOptions: SubscriberOption[];
    setSubscriberOptions: Dispatch<SetStateAction<SubscriberOption[]>>;
    viewAsSupplier?: string;
}

export function BulkEditDevicesEdit({
    subscriberOptions,
    setSubscriberOptions,
    viewAsSupplier,
}: BulkEditDevicesEditProps) {
    const [groupOptions, setGroupOptions] = useState<FormSelectOption[]>([
        { value: 'None' },
    ]);

    const userOptions = useMemo(() => subscriberOptions, [subscriberOptions]);

    const { user } = useAuth();
    const { watch } = useFormContext();
    const device = watch() as BulkDevicesInput;

    useEffect(() => {
        const supplierId = viewAsSupplier ?? user?.protected?.supplierId;
        if (supplierId !== undefined) {
            getGroups(supplierId).then((groups) => {
                const newGroupOptions = groups.map((g) => ({
                    value: g.name,
                }));
                setGroupOptions([{ value: 'None' }, ...newGroupOptions]);
            });
        }
    }, [user?.protected?.supplierId, viewAsSupplier]);

    const fttTimeOptions: FormSelectOption[] = useMemo(() => {
        let currentTime = new Date();

        return [
            { value: -1, name: 'Default' },
            ...Array.from({ length: 24 }, () => {
                const value = currentTime.getUTCHours() * 3600;
                const out = {
                    name: new Date(
                        currentTime.setMinutes(0, 0, 0)
                    ).toLocaleTimeString('en-us', {
                        hour: 'numeric',
                        minute: '2-digit',
                    }),
                    value,
                };
                currentTime = new Date(currentTime.getTime() + 60 * 60 * 1000);
                return out;
            }),
        ];
    }, []);

    const rIntTimeOptions: FormSelectOption[] = [
        { value: 1 * 60 * 60, name: '1 Hour' },
        { value: 2 * 60 * 60, name: '2 Hours' },
        { value: 3 * 60 * 60, name: '3 Hours' },
        { value: 4 * 60 * 60, name: '4 Hours' },
        { value: 6 * 60 * 60, name: '6 Hours (default)' },
        { value: 12 * 60 * 60, name: '12 Hours' },
        { value: 24 * 60 * 60, name: '24 Hours' },
        { value: 48 * 60 * 60, name: '48 Hours' },
        { value: 72 * 60 * 60, name: '72 Hours' },
        { value: 96 * 60 * 60, name: '96 Hours' },
    ];

    const tIntTimeOptions: FormSelectOption[] = [
        { value: 1 * 60 * 60, name: '1 Hour' },
        { value: 2 * 60 * 60, name: '2 Hours' },
        { value: 3 * 60 * 60, name: '3 Hours' },
        { value: 4 * 60 * 60, name: '4 Hours' },
        { value: 6 * 60 * 60, name: '6 Hours' },
        { value: 12 * 60 * 60, name: '12 Hours' },
        { value: 24 * 60 * 60, name: '24 Hours (default)' },
        { value: 48 * 60 * 60, name: '48 Hours' },
        { value: 72 * 60 * 60, name: '72 Hours' },
        { value: 96 * 60 * 60, name: '96 Hours' },
    ];

    return (
        <FormSection title="Edit">
            <Typography variant="h6">Alert Settings</Typography>
            {device.users.enabled && (
                <SubscriberList
                    userOptions={userOptions}
                    setSubscriberOptions={setSubscriberOptions}
                />
            )}
            <FormInputGroup>
                <FormText
                    field="tcov.value"
                    label="Change of Tank Level Since Last Transmission (%)"
                    disabled={!device.tcov.enabled}
                    isNumber
                />
                <FormText
                    field="thresh1.value"
                    label="Warning Threshold (%)"
                    disabled={!device.thresh1.enabled}
                    isNumber
                />
            </FormInputGroup>
            <FormInputGroup>
                <FormText
                    field="thresh2.value"
                    label="Critical Threshold (%)"
                    disabled={!device.thresh2.enabled}
                    isNumber
                />
                <FormCheckbox
                    field="thresh_enabled.value"
                    label="Alerts Enabled"
                    disabled={!device.thresh_enabled.enabled}
                />
            </FormInputGroup>
            <Typography variant="h6">Device Settings</Typography>
            <FormInputGroup>
                <FormSelect
                    field="group.value"
                    label="Group"
                    disabled={!device.group.enabled}
                    options={groupOptions}
                />
                <FormSelect
                    field="ftt.value"
                    label="Transmission Time"
                    disabled={!device.ftt.enabled}
                    options={fttTimeOptions}
                />
            </FormInputGroup>
            <Typography variant="h6">
                Reading & Transmission Settings
            </Typography>
            <FormInputGroup>
                <FormSelect
                    field="r_int.value"
                    label="Reading Interval"
                    disabled={!device.r_int.enabled}
                    options={rIntTimeOptions}
                />
            </FormInputGroup>
            <FormInputGroup>
                <FormSelect
                    field="t_int.value"
                    label="Reporting Interval"
                    disabled={!device.t_int.enabled}
                    options={tIntTimeOptions}
                />
            </FormInputGroup>
        </FormSection>
    );
}

interface SubscriberListProps {
    userOptions: SubscriberOption[];
    setSubscriberOptions: Dispatch<SetStateAction<SubscriberOption[]>>;
}

function SubscriberList({
    userOptions,
    setSubscriberOptions,
}: SubscriberListProps) {
    const [currentPage, setCurrentPage] = useState(1);
    const [subscribersPerPage] = useState(5);
    const indexOfLastSubscriber = currentPage * subscribersPerPage;
    const indexOfFirstSubscriber = indexOfLastSubscriber - subscribersPerPage;

    const [searchQuery, setSearchQuery] = useState('');

    const [enabledSubscribersOnly, setEnabledSubscribersOnly] = useState(false);

    const filteredSubscribers = userOptions.filter((subscriber) => {
        if (enabledSubscribersOnly) {
            return (
                subscriber.value
                    .toString()
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) &&
                (subscriber.checked || subscriber.indeterminate)
            );
        }

        return subscriber.value
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
    });

    const currentSubscribers = filteredSubscribers.slice(
        indexOfFirstSubscriber,
        indexOfLastSubscriber
    );

    // Function to change page
    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    const { setValue } = useFormContext();

    const updateSubscriberOptions = (opt: SubscriberOption) => {
        const added: string[] = [];
        const removed: string[] = [];
        setSubscriberOptions((prevOpts: SubscriberOption[]) => {
            const newSubscribers = prevOpts.map((subscriber) => {
                // Check if the current subscriber in the map is the one being updated
                if (subscriber.value === opt.value) {
                    // Logic for handling the indeterminate and checked states
                    if (subscriber.hasIndeterminate) {
                        if (!subscriber.checked && !subscriber.indeterminate) {
                            return {
                                ...subscriber,
                                indeterminate: true,
                                checked: false,
                            };
                        } else if (
                            !subscriber.checked &&
                            subscriber.indeterminate
                        ) {
                            return {
                                ...subscriber,
                                checked: true,
                                indeterminate: false,
                            };
                        } else if (subscriber.checked) {
                            return {
                                ...subscriber,
                                checked: false,
                                indeterminate: false,
                            };
                        }
                    } else {
                        return {
                            ...subscriber,
                            checked: !subscriber.checked,
                        };
                    }
                }

                // Return all other subscribers as is
                return subscriber;
            });

            //
            newSubscribers.forEach((opt) => {
                if (opt.checked) {
                    added.push(opt.value.toString());
                }
                if (
                    !opt.checked &&
                    !opt.indeterminate &&
                    opt.hasIndeterminate
                ) {
                    removed.push(opt.value.toString());
                }
            });

            setValue('addedSubscribers.value', added);
            setValue('removedSubscribers.value', removed);

            return newSubscribers;
        });
    };

    return (
        <Card sx={{ display: 'block', paddingTop: 0 }}>
            <Paper
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '15px',
                    fontSize: '14px',
                    width: '450px',
                }}
            >
                <List>
                    <ListSubheader>Subscriber Icon Legend</ListSubheader>
                    <ListItem>
                        <CheckBox />
                        <ArrowRightAlt />
                        User you want added to all selected tanks
                    </ListItem>
                    <ListItem>
                        <CheckBoxOutlineBlank />
                        <ArrowRightAlt />
                        User you want removed from all selected tanks
                    </ListItem>
                    <ListItem>
                        <IndeterminateCheckBoxOutlined />
                        <ArrowRightAlt />
                        User currently included on some selected tanks
                    </ListItem>
                </List>
            </Paper>
            <CardContent>
                <TableContainer component={Paper}>
                    <Box
                        id="enabledSubscribersOnlyFilter"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%" // Make sure it occupies the full width
                        m={1}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabledSubscribersOnly}
                                    onChange={(e) =>
                                        setEnabledSubscribersOnly(
                                            e.target.checked
                                        )
                                    }
                                    name="enabledSubscribersOnly"
                                    color="primary"
                                />
                            }
                            label="Show Enabled Subscribers Only"
                        />
                    </Box>
                    <Table
                        sx={{ minWidth: 650 }}
                        aria-label="subscribers table"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                    Alert Enabled
                                </TableCell>
                                <TableCell>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-start"
                                    >
                                        <span>Subscribers</span>
                                        <TextField
                                            label="Search"
                                            variant="outlined"
                                            size="small"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                setCurrentPage(1); // Reset to first page on search
                                            }}
                                            sx={{ ml: 2, width: '30%' }} // Adjust margins and width as needed
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentSubscribers.map((opt) => (
                                <TableRow
                                    key={opt.value}
                                    sx={{
                                        '&:last-child td, &:last-child th': {
                                            border: 0,
                                        },
                                    }}
                                >
                                    <TableCell align="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={opt.checked}
                                                    indeterminate={
                                                        opt.indeterminate
                                                    }
                                                    onChange={() => {
                                                        updateSubscriberOptions(
                                                            opt
                                                        );
                                                    }}
                                                />
                                            }
                                            label=""
                                        />
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {opt.value}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                        count={Math.ceil(
                            filteredSubscribers.length / subscribersPerPage
                        )}
                        page={currentPage}
                        onChange={(event, value) => paginate(value)}
                        color="primary"
                        shape="rounded"
                    />
                </Box>
            </CardContent>
        </Card>
    );
}
